/**
 * Admin Security page — TOTP 2FA management.
 *
 * Flow:
 *  1. Idle · "Enable 2FA" button
 *  2. Setup  · shows QR code + secret, asks for 6-digit code
 *  3. Verified · shows 10 one-time recovery codes (once), user must save them
 *  4. Enabled · shows status + Disable / Regenerate / Revoke-device controls
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  ShieldOff,
  KeyRound,
  QrCode,
  Copy,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Trash2,
  Loader2,
} from "lucide-react";
import { TwoFactor, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AdminSecurity() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Setup wizard state
  const [setupData, setSetupData] = useState(null); // { qr_png_base64, otpauth_uri, account }
  const [setupCode, setSetupCode] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);

  // Recovery codes (shown once after enable / regenerate)
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable dialog state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);

  // Regenerate dialog state
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => {
    if (!tokenStorage.get()) {
      nav("/admin", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const s = await TwoFactor.status();
      setStatus(s);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin", { replace: true });
      setError("Could not load 2FA status. Please retry.");
    } finally {
      setLoading(false);
    }
  }

  async function startSetup() {
    setError("");
    setNotice("");
    setSetupBusy(true);
    try {
      const d = await TwoFactor.setup();
      setSetupData(d);
      setSetupCode("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start setup");
    } finally {
      setSetupBusy(false);
    }
  }

  async function verifySetup(e) {
    e?.preventDefault?.();
    setError("");
    setSetupBusy(true);
    try {
      const r = await TwoFactor.verifySetup(setupCode.trim());
      setRecoveryCodes(r.recovery_codes || []);
      setSetupData(null);
      setSetupCode("");
      setNotice("2FA is now enabled. Save your recovery codes below — they will not be shown again.");
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code — please try again.");
    } finally {
      setSetupBusy(false);
    }
  }

  async function disable2fa(e) {
    e?.preventDefault?.();
    setError("");
    setDisableBusy(true);
    try {
      await TwoFactor.disable(disablePassword, disableCode.trim());
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      setRecoveryCodes(null);
      setNotice("Two-factor authentication has been disabled on your account.");
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Disable failed. Check your password + code.");
    } finally {
      setDisableBusy(false);
    }
  }

  async function regenerateCodes(e) {
    e?.preventDefault?.();
    setError("");
    setRegenBusy(true);
    try {
      const r = await TwoFactor.regenerateRecovery(regenCode.trim());
      setRecoveryCodes(r.recovery_codes || []);
      setShowRegenerate(false);
      setRegenCode("");
      setNotice("New recovery codes issued. Your previous codes are no longer valid.");
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not regenerate. Check your authenticator code.");
    } finally {
      setRegenBusy(false);
    }
  }

  async function revokeDevices() {
    setError("");
    try {
      const r = await TwoFactor.revokeDevices();
      setNotice(`Revoked ${r.revoked} trusted device(s). Every browser will now require 2FA.`);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not revoke devices.");
    }
  }

  function copyCodes() {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setNotice("Copied recovery codes to clipboard.");
  }

  function downloadCodes() {
    if (!recoveryCodes) return;
    const blob = new Blob(
      [
        "Sahyog Admin — 2FA Recovery Codes\n",
        `Generated: ${new Date().toISOString()}\n\n`,
        "Each code works exactly once. Store them somewhere safe (password manager).\n",
        "If you lose access to your authenticator, use these to sign in.\n\n",
        recoveryCodes.map((c, i) => `${String(i + 1).padStart(2, "0")}.  ${c}`).join("\n"),
        "\n",
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sahyog-admin-2fa-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      data-testid="admin-security-page"
      className="min-h-screen bg-[#04102B] text-white"
    >
      <header className="border-b border-white/10 backdrop-blur-lg bg-black/20 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => nav("/admin/leads")}
            data-testid="security-back"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788]"
          >
            <ArrowLeft size={14} /> Back to Admin
          </button>
          <div className="flex items-center gap-3">
            <Logo size={40} testId="security-logo" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
              Security · Two-Factor Auth
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-8">
        <section>
          <h1 className="font-display text-3xl md:text-4xl">Account Security</h1>
          <p className="mt-2 text-white/60 text-sm leading-relaxed max-w-2xl">
            Add a second layer of protection to your admin account. Once enabled, every sign-in
            requires your password <em>and</em> a rotating 6-digit code from an authenticator app
            (Google Authenticator, 1Password, Authy, Microsoft Authenticator).
          </p>
        </section>

        {notice && (
          <div
            data-testid="security-notice"
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-sm text-emerald-100 flex items-start gap-3"
          >
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{notice}</div>
          </div>
        )}

        {error && (
          <div
            data-testid="security-error"
            className="rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-200 flex items-start gap-3"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <div>{typeof error === "string" ? error : JSON.stringify(error)}</div>
          </div>
        )}

        {/* ---------- Status card ---------- */}
        <section
          data-testid="security-status-card"
          className="rounded-3xl border border-[#D4AF37]/25 bg-[#0A1F44] p-7 md:p-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
              {status?.enabled ? (
                <ShieldCheck size={22} className="text-[#F0D788]" />
              ) : (
                <ShieldOff size={22} className="text-white/60" />
              )}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.22em] text-white/50">Status</div>
              <div className="mt-1 font-display text-2xl">
                {loading ? (
                  <span className="inline-flex items-center gap-2 text-white/60"><Loader2 size={18} className="animate-spin" /> Loading…</span>
                ) : status?.enabled ? (
                  <span data-testid="security-status-enabled" className="text-emerald-300">Two-factor enabled</span>
                ) : (
                  <span data-testid="security-status-disabled" className="text-white/80">Two-factor not enabled</span>
                )}
              </div>
              {status?.enabled && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-black/25 border border-white/10 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Recovery codes remaining</div>
                    <div className="mt-1 text-lg font-semibold text-[#F0D788]" data-testid="security-recovery-count">{status.recovery_codes_remaining}</div>
                  </div>
                  <div className="rounded-xl bg-black/25 border border-white/10 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Trusted devices</div>
                    <div className="mt-1 text-lg font-semibold text-[#F0D788]" data-testid="security-trusted-count">{status.trusted_devices}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            {!loading && !status?.enabled && !setupData && (
              <button
                type="button"
                onClick={startSetup}
                disabled={setupBusy}
                data-testid="security-enable-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
              >
                {setupBusy ? <><Loader2 size={14} className="animate-spin" /> Preparing…</> : <><ShieldCheck size={14} /> Enable 2FA</>}
              </button>
            )}
            {status?.enabled && (
              <>
                <button
                  type="button"
                  data-testid="security-regenerate-btn"
                  onClick={() => setShowRegenerate(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/40 text-sm text-[#F0D788] hover:bg-[#D4AF37]/10"
                >
                  <RefreshCw size={14} /> Regenerate recovery codes
                </button>
                <button
                  type="button"
                  data-testid="security-revoke-devices-btn"
                  onClick={revokeDevices}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 text-sm text-white/85 hover:bg-white/[0.06]"
                >
                  <Trash2 size={14} /> Revoke trusted devices
                </button>
                <button
                  type="button"
                  data-testid="security-disable-btn"
                  onClick={() => setShowDisable(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-500/40 text-sm text-red-200 hover:bg-red-500/10"
                >
                  <ShieldOff size={14} /> Disable 2FA
                </button>
              </>
            )}
          </div>
        </section>

        {/* ---------- Setup wizard ---------- */}
        {setupData && (
          <section
            data-testid="security-setup-panel"
            className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-7 md:p-8"
          >
            <div className="flex items-center gap-2.5 text-[#F0D788]">
              <QrCode size={16} />
              <span className="text-xs tracking-[0.22em] uppercase">Step 1 · Scan the QR code</span>
            </div>
            <p className="mt-3 text-sm text-white/75 leading-relaxed">
              Open your authenticator app and add a new account by scanning the QR code below, or paste
              the setup key manually.
            </p>

            <div className="mt-6 grid md:grid-cols-[240px_1fr] gap-6 items-start">
              <div className="rounded-2xl bg-white p-3 w-fit">
                <img
                  src={setupData.qr_png_base64}
                  data-testid="security-qr"
                  alt="Scan this QR code with your authenticator app"
                  className="w-56 h-56 block"
                />
              </div>
              <div className="space-y-3 text-sm">
                <div className="text-xs uppercase tracking-[0.22em] text-white/60">Setup key (manual entry)</div>
                <div
                  data-testid="security-setup-uri"
                  className="rounded-xl bg-black/40 border border-[#D4AF37]/20 px-4 py-3 font-mono text-[11px] break-all text-[#FBE9A2]"
                >
                  {setupData.otpauth_uri}
                </div>
                <div className="text-xs text-white/70">
                  Account: <span className="text-white">{setupData.account}</span>
                </div>

                <form onSubmit={verifySetup} className="mt-6 space-y-3">
                  <div className="text-xs tracking-[0.22em] uppercase text-[#F0D788] flex items-center gap-2">
                    <KeyRound size={13} /> Step 2 · Enter the 6-digit code
                  </div>
                  <input
                    data-testid="security-setup-code"
                    required
                    autoFocus
                    inputMode="numeric"
                    pattern="[0-9 ]{6,10}"
                    maxLength={10}
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/25 text-white outline-none focus:border-[#D4AF37] tracking-widest text-center text-lg"
                    placeholder="123 456"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      data-testid="security-setup-verify"
                      disabled={setupBusy || setupCode.trim().length < 6}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
                    >
                      {setupBusy ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : <><ShieldCheck size={14} /> Verify &amp; enable</>}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSetupData(null); setSetupCode(""); }}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-xs text-white/70 hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        )}

        {/* ---------- Recovery codes reveal ---------- */}
        {recoveryCodes && recoveryCodes.length > 0 && (
          <section
            data-testid="security-recovery-panel"
            className="rounded-3xl border border-emerald-500/40 bg-emerald-500/[0.06] p-7 md:p-8"
          >
            <div className="flex items-center gap-2.5 text-emerald-300">
              <CheckCircle2 size={18} />
              <span className="text-xs tracking-[0.22em] uppercase">Save these recovery codes</span>
            </div>
            <p className="mt-3 text-sm text-white/85 leading-relaxed">
              Each code works exactly once. Store them in your password manager. If you lose your
              authenticator, they are the only way to regain access. They will <strong>not</strong>{" "}
              be shown again.
            </p>
            <div
              data-testid="security-recovery-list"
              className="mt-5 grid sm:grid-cols-2 gap-2 font-mono text-sm"
            >
              {recoveryCodes.map((c, i) => (
                <div
                  key={c}
                  className="rounded-lg border border-white/10 bg-black/30 px-4 py-2.5 flex items-center gap-3"
                >
                  <span className="text-white/40 text-xs">{String(i + 1).padStart(2, "0")}.</span>
                  <span className="text-[#F0D788] tracking-wider">{c}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="security-recovery-copy"
                onClick={copyCodes}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/40 text-sm text-[#F0D788] hover:bg-[#D4AF37]/10"
              >
                <Copy size={13} /> Copy all
              </button>
              <button
                type="button"
                data-testid="security-recovery-download"
                onClick={downloadCodes}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-sm text-white/85 hover:bg-white/[0.06]"
              >
                <Download size={13} /> Download .txt
              </button>
              <button
                type="button"
                data-testid="security-recovery-dismiss"
                onClick={() => setRecoveryCodes(null)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white/60 hover:text-white ml-auto"
              >
                I&apos;ve saved them
              </button>
            </div>
          </section>
        )}
      </main>

      {/* ---------- Disable dialog ---------- */}
      {showDisable && (
        <ModalShell onClose={() => setShowDisable(false)} title="Disable two-factor authentication" testId="security-disable-modal">
          <form onSubmit={disable2fa} className="space-y-4">
            <p className="text-white/70 text-sm leading-relaxed">
              Disabling 2FA will remove all trusted devices and delete your existing recovery codes.
              To confirm, enter your <strong>current password</strong> and a code from your authenticator.
            </p>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Current password</div>
              <input
                data-testid="security-disable-password"
                type="password"
                required
                autoFocus
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white"
              />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Authenticator code</div>
              <input
                data-testid="security-disable-code"
                required
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                inputMode="numeric"
                maxLength={10}
                className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white tracking-widest text-center"
                placeholder="123 456"
              />
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowDisable(false)}
                className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="security-disable-confirm"
                disabled={disableBusy || !disablePassword || disableCode.trim().length < 6}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-60"
              >
                {disableBusy ? <><Loader2 size={13} className="animate-spin" /> Disabling…</> : <><ShieldOff size={13} /> Disable 2FA</>}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {/* ---------- Regenerate dialog ---------- */}
      {showRegenerate && (
        <ModalShell onClose={() => setShowRegenerate(false)} title="Regenerate recovery codes" testId="security-regenerate-modal">
          <form onSubmit={regenerateCodes} className="space-y-4">
            <p className="text-white/70 text-sm leading-relaxed">
              Generating new codes will invalidate any previously issued recovery codes. Enter a
              current code from your authenticator to continue.
            </p>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Authenticator code</div>
              <input
                data-testid="security-regenerate-code"
                required
                autoFocus
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value)}
                inputMode="numeric"
                maxLength={10}
                className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white tracking-widest text-center"
                placeholder="123 456"
              />
            </label>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowRegenerate(false)}
                className="px-4 py-2 rounded-full border border-white/15 text-xs text-white/75"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="security-regenerate-confirm"
                disabled={regenBusy || regenCode.trim().length < 6}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full btn-gold text-sm font-semibold disabled:opacity-60"
              >
                {regenBusy ? <><Loader2 size={13} className="animate-spin" /> Working…</> : <><RefreshCw size={13} /> Regenerate</>}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}

function ModalShell({ title, testId, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid={testId} className="w-full max-w-md rounded-3xl bg-[#0A1F44] border border-[#D4AF37]/30 shadow-2xl">
        <div className="flex items-center justify-between px-6 pt-6">
          <div className="font-display text-lg text-white">{title}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-sm">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
